import * as Crypto from 'expo-crypto'
import { Platform } from 'react-native'
import { all, select, call, put, spawn, putResolve, race } from 'typed-redux-saga'
import { delay, take } from 'redux-saga/effects'
import type { SagaIterator, Task } from 'redux-saga'
import DeviceInfo from 'APP/Helpers/DeviceInfo'
import compareVersions from 'compare-versions'
import { getTimeSinceStartup } from 'react-native-startup-time'
import Analytics, { LAUNCH_CATEGORY } from 'APP/Services/Analytics'
import Alert from 'APP/Converse/Alert'
import { navigationRef as Nav } from 'APP/Nav'
import { PlatformType, DeactivateTokenRequestV2 } from '@dialogue/engage'

// Actions
import LoginActions, { LoginTypes } from 'APP/Redux/LoginRedux'
import PatientActions from 'APP/Redux/PatientRedux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import AppSessionActions from 'APP/Redux/AppSessionRedux'
import StartupActions, { StartupTypes } from 'APP/Redux/StartupRedux'
import DeeplinkActions, { DeeplinkTypes } from 'APP/Redux/DeeplinkRedux'
import HabitActions from 'APP/Redux/HabitsRedux'
import { familyActions } from 'APP/Store/Family'
import { memberTasks } from 'APP/Store/MemberTasks'
import { checkAppleHealthDisconnectedStatus } from 'APP/Sagas/TerraSagas'
import { isIOS } from 'APP/Helpers/checkPlatform'
import {
  classifyStartupFailure,
  createVersionNotSupportedFailure,
} from 'APP/Helpers/StartupFailures'

// Sagas
import {
  logout,
  checkAppLockSupport,
  toggleMfa,
  checkPublicComputer,
  login,
  renewAuth,
  registerTokens,
  MISSING_REFRESH_TOKEN_ERROR_CODE,
} from './AuthSagas'
import {
  startupNavigation,
  updateLocation,
  requestMessagingCredentials,
  changeEmailAuthorized,
  fetchUserInfo,
} from './PatientSagas'
import { shouldShowAccountLinking } from 'APP/Lib/AccountLinkingHelpers'
import { fetchAppointments } from './PatientHistorySagas'
import { featuresMonitor } from './FeaturesSaga'
import { watchDeeplinkRequests, getPrimaryConnection } from './DeeplinkSaga'
import { getActiveMinutes } from './ActiveMinutesSagas'
import { registerDeviceToken } from 'APP/Store/Device/sagas'
import { getTokensApi } from 'APP/Store/utils/engage'

import I18n from 'APP/I18n'
import { waitForLDInitialization, ldClient } from 'APP/Services/LaunchDarkly'
import Config from 'APP/Config'
import { APP_LOCK_STATE } from 'APP/Lib/AppLock'
import { logoutInsiderUser } from 'APP/Services/Insider'
import sprigClient from 'APP/Services/Sprig'

import { hasLoginHash, popDeferredDeeplink } from 'APP/Lib/Utilities'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { logger } from 'APP/Lib/Datadog'
import { peekDestinationOrigin } from 'APP/Lib/SeamlessDestination'
import { isHeadlessTask } from 'APP/Lib/HeadlessTask'
import * as BrownfieldBridgeInternal from 'APP/BrownfieldBridge/BrownfieldBridgeInternal'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { selectSkipTokenExchange } from 'APP/Store/Brownfield'
import { isVideoCallRoute } from 'APP/Store/VideoSession/sagas'
import type { RootState } from 'APP/Store/CreateStore'
import type { AppSessionState } from 'APP/Store/AppSession/types'
import type { DeeplinkState } from 'APP/Store/Deeplink/types'
import type { LoginState } from 'APP/Store/Login/types'
import type { PatientState, PatientProfileState } from 'APP/Store/Patient/types'
import type { PreloadState } from 'APP/Store/Preload'
import type { StartupState } from 'APP/Store/Startup/types'

export const startupState = (state?: RootState): StartupState => state?.app ?? ({} as StartupState)

export function* waitForNavigationReady() {
  const { navigationReady } = yield* select(startupState)
  if (!navigationReady) {
    yield take(StartupTypes.SET_NAVIGATION_READY)
  }
}

export const loginState = (state?: RootState) => state?.login || ({} as LoginState)

export const preloadState = (state?: RootState) => state?.preload || ({} as PreloadState)
export const patientState = (state?: RootState) => state?.patient || ({} as PatientState)
export const patientProfileState = (state?: RootState) =>
  state?.patient?.profile || ({} as PatientProfileState)
export const appSessionState = (state?: RootState) => state?.appSession || ({} as AppSessionState)
export const deeplinkState = (state?: RootState) => state?.deeplink || ({} as DeeplinkState)

export function* isConsentNeeded() {
  const patient = yield* select(patientState)
  // null means the consent check hasn't run yet — treat as consent-needed in embedded
  // to avoid bypassing the consent screen before the API result is available
  return patient.hasCurrentConsentVersion !== true
}

// Config'
export const REGISTER_PUSH_TOKEN_RETRY_LIMIT = 5
export const LOCKED_TIME_LIMIT_IN_MILLIS = __DEV__ ? 10000 : 60000
// 1 min session expiry in dev, 3 hours in prod.
// Note that since we're comparing against token expiry we need a negative delta in dev (resolves to 1 min since token was issued)
export const FORCED_SESSION_INACTIVITY_TIME_MILLIS = __DEV__ ? -3540000 : 10800000

const PLATFORM_PUSH_TYPES: Partial<Record<typeof Platform.OS, PlatformType>> = {
  ios: PlatformType.Apns,
  android: PlatformType.Fcm,
}

export function* loginWithExchangeToken() {
  const exchangeToken = yield* call(BrownfieldBridgeInternal.getExchangeToken)

  yield* call(
    login,
    { triggerLogoutOnFailure: true, triggerStartupNav: true },
    { exchangeToken: exchangeToken, type: LoginTypes.LOGIN_REQUEST_WITH_EXCHANGE_TOKEN }
  )
}

export function* enableAppLock() {
  const appLockState = yield* call(checkAppLockSupport)
  if (appLockState === APP_LOCK_STATE.ENABLED_BLOCKED) {
    //Log out & clear app lock enabled state
    yield* call(logout, { reason: 'App lock enabled blocked' })
  } else {
    if (Nav.getCurrentRoute()?.name !== 'videoCall') {
      const app = yield* select(appSessionState)
      const backgroundTimestamp = app?.backgroundTimestamp || 0
      const now = yield* call(getTimeSinceStartup)
      const diffInMillis = now - backgroundTimestamp

      if (diffInMillis > LOCKED_TIME_LIMIT_IN_MILLIS) {
        switch (appLockState) {
          case APP_LOCK_STATE.ENABLED:
            //Trigger auth

            // TODO: Verify, should this actually be a replace?
            yield* call(Nav.navigate, 'applock')
            break
          case APP_LOCK_STATE.DISABLED:
            //Do nothing
            break
        }
      }
    }
  }
}

export function* launchStartupSequence() {
  try {
    yield* call(renewAuth)
  } catch (err) {
    const skipTokenExchange = yield* select(selectSkipTokenExchange)
    const { preloaded } = yield* select(preloadState)
    // Only fall back to loginWithExchangeToken when neither VoIP cold-start
    // (skipTokenExchange) nor a preload has already consumed the exchange token.
    if (isEmbeddedEnvironment() && !skipTokenExchange && !preloaded) {
      yield* call(loginWithExchangeToken)
    } else {
      throw err
    }
  }

  yield* put(LoginActions.startRefreshAuthPoll())

  yield* call(startupNavigation)

  Analytics.trackLoadingTime({
    action: LAUNCH_CATEGORY.EXIT,
    label: 'Startup navigation finished',
  })

  const patientProfile = yield* select(patientProfileState)
  if (patientProfile.id) {
    yield* call(requestMessagingCredentials)
  }
}

export function* handleLaunchStartupSequence() {
  try {
    yield* call(launchStartupSequence)
  } catch (err) {
    if ((err as any)?.code === MISSING_REFRESH_TOKEN_ERROR_CODE) {
      // renewAuth already dispatched logout — just swallow to prevent crashing the root saga.
      return
    }
    throw err
  }
}

// process STARTUP actions
export function* startup() {
  if (isHeadlessTask()) {
    return
  }

  // Full initialization of the app.
  // Make sure that part of the initialization only happens once.
  // In embedded mode, we allow re-entry because the host app can restart the
  // activity/view controller while the RN bridge stays alive — we need to
  // re-run startup to restore navigation and re-register push tokens. The
  // `fullyInitialized` flag is filtered from redux-persist so it always starts
  // false on a fresh JS context, which is what kicks off fullStartup here.
  const { fullyInitialized } = yield* select(startupState)
  if (!fullyInitialized) {
    yield* putResolve(StartupActions.fullStartup())

    return
  }

  // Android can destroy MainActivity while the JS context stays warm (Back at the task root,
  // "don't keep activities", OS reclaim). React remounts on `loading` with fullyInitialized
  // already true, so nothing would navigate away from the spinner unless we re-run startup.
  if (Platform.OS === 'android' && !isEmbeddedEnvironment()) {
    if (!Nav.isReady()) {
      yield* put(StartupActions.resetNavigationReady())
    }
    yield* call(waitForNavigationReady)
    if (Nav.getCurrentRoute()?.name === 'loading') {
      yield* putResolve(StartupActions.fullStartup())
    }
    return
  }

  // Else, if initialized, ensure embedded environments are setup correctly
  if (isEmbeddedEnvironment()) {
    // Only reset navigation readiness when the NavigationContainer has
    // actually unmounted (navigationRef stale).  If the React tree is still
    // alive (e.g. user popped back to shell-app and re-entered), the ref is
    // still valid and we must NOT reset — otherwise waitForNavigationReady
    // would block forever because onReady won't fire again.
    if (!Nav.isReady()) {
      yield* put(StartupActions.resetNavigationReady())
    }
    yield* put(DeeplinkActions.setReadyToProcess(false))

    // Re-register the native bridge so it can emit events (e.g. openDeeplink)
    // to JS again. The previous DialogueActivity destruction may have left the
    // native module holding a stale ReactApplicationContext.
    BrownfieldBridgeInternal.reconnectBridge()

    yield* call(waitForNavigationReady)

    const needsAccountLinking = yield* select(shouldShowAccountLinking)
    if (needsAccountLinking) return

    const needsConsent = yield* call(isConsentNeeded)
    if (needsConsent) {
      yield* call(Nav.reset, {
        index: 0,
        routes: [{ name: 'consent', params: { flow: 'startupFlow' } }],
      })
      return
    }

    // If a call screen is already presented, skip the deeplink-vs-tabbar navigation below —
    // resetting to the tabbar or following a deeplink would pull the user out of the call.
    const onCallScreen = yield* call(isVideoCallRoute)

    // Subscribe to Branch regardless of the call screen so deeplinks — queued in Branch.js while
    // the subscriber was absent, or received after the call while we stay in the app — are resolved
    // through the standard Branch flow instead of being lost.
    if (!watchDeeplinkRequestsTask) {
      watchDeeplinkRequestsTask = yield* spawn(watchDeeplinkRequests)
    }

    if (!onCallScreen) {
      // Give Branch a short window to resolve any pending deeplink URL.
      // If a deeplink arrives, navigate directly to the destination
      // instead of flashing the tabbar first.
      const { deeplink } = yield* race({
        deeplink: take(DeeplinkTypes.SET_DEFERRED_DEEPLINK),
        timeout: delay(500),
      })

      if (!deeplink) {
        yield* call(Nav.reset, { index: 0, routes: [{ name: 'tabbar' }] })
      }
    }

    // Android launches a new Activity every time we cross the bridge,
    // meaning startup() runs every time. Because of that behavior, once
    // the embedded engine is warmed up, the following lines cause a double navigation.
    // The only benefit for iOS is that this makes deeplinks appear faster,
    // but there is no visible effect on Android.

    if (Platform.OS === 'ios') {
      yield* put(DeeplinkActions.setReadyToProcess(true))

      if (!onCallScreen) {
        const { path: deferredPath } = yield* select(deeplinkState)

        if (deferredPath) {
          yield* put(DeeplinkActions.processDeferredDeeplink())
        }
      }
    }
  }
}

export function* appVersionIsSupported(deviceInfo: Partial<typeof DeviceInfo>) {
  if (Platform.OS === 'web') return true

  // Fetch the minimum supported versions
  const supportedVersionsResponse = yield* call(
    [ldClient, ldClient.jsonVariation<{ min_version: string }>],
    `${Config.BRAND_ID}-supported-version`,
    null
  )
  if (supportedVersionsResponse && deviceInfo.getVersion) {
    const minVersion = supportedVersionsResponse.min_version
    const nativeAppVersion = deviceInfo.getVersion()
    if (compareVersions(nativeAppVersion, minVersion) === -1) {
      return false
    }
  }
  return true
}

export function* checkAppLock() {
  const appLockState = yield* call(checkAppLockSupport)

  switch (appLockState) {
    case APP_LOCK_STATE.ENABLED_BLOCKED:
      Analytics.trackLoadingTime({
        action: LAUNCH_CATEGORY.EXIT,
        label: 'App Lock blocked',
      })
      //Log out & clear app lock enabled state
      yield* call(logout, { reason: 'App lock enabled blocked' })
      break
    case APP_LOCK_STATE.ENABLED:
      //Trigger auth
      Analytics.trackLoadingTime({ action: 'App lock auth triggered' })
      // TODO: Verify, should this actually be a replace?
      yield* call(Nav.navigate, 'applock')
      break
    case APP_LOCK_STATE.DISABLED:
      yield* call(launchStartupSequence)
      break
  }
}

export function* forcedSessionInactivity() {
  const { forcedSessionInactivityRequired } = yield* select(patientProfileState)

  if (forcedSessionInactivityRequired) {
    const { tokenExpiration } = yield* select(loginState)
    const diffInMillis = Date.now() - (tokenExpiration || 0)

    if (diffInMillis > FORCED_SESSION_INACTIVITY_TIME_MILLIS) {
      logger.warn('auth:logout_forced_inactivity', {
        diff_ms: diffInMillis,
        threshold_ms: FORCED_SESSION_INACTIVITY_TIME_MILLIS,
      })
      yield* call(logout, { reason: 'Forced session inactivity' })

      Alert.alert(
        I18n.t('Common.forcedSessionInactivity.alertTitle'),
        I18n.t('Common.forcedSessionInactivity.alertDescription') ?? '',
        undefined,
        { cancelable: false }
      )
      // Indicate that the user was logged out
      return true
    }
  }

  return false
}

let watchDeeplinkRequestsTask: Task | undefined

export function* fullStartup(action?: { type: string }) {
  try {
    const hash = window?.location?.hash
    const loginHashDetected = isWeb() && hash && hasLoginHash(hash)

    // After saga send login request to auth0 it redirects with access token in hash
    // We capture it and process.
    if (loginHashDetected) {
      yield* call(login, { withHash: true })

      // Funnel end (web) — pairs with seamless_sso_started. The seamless SSO login completes
      // here on web via the token hash, whether the IdP had an active session (instant) or the
      // member just logged in. Native completes in-place in AuthSagas.login instead. Peek (not
      // consume) so navigation still gets the stash; gate on an established session so a failed
      // parseHash doesn't count.
      const { accessToken: seamlessAccessToken } = yield* select(loginState)
      if (seamlessAccessToken && peekDestinationOrigin() === 'seamless') {
        logger.info('seamless_sso_success', { connection: getPrimaryConnection() })
      }

      const {
        profile: { email },
        changeEmailToken: { new_email, token },
      } = yield* select(patientState)

      // If a change-email token is pending (set by the verifyemail deeplink before
      // the Auth0 redirect), complete the email change and skip normal startup.
      if ((email || new_email) && token) {
        yield* call(changeEmailAuthorized, token, new_email)
        return
      }

      // The Auth0 redirect wipes the deeplink path. We restore the deeplink
      // from before the redirect so startup navigation can follow it.
      const restoredDeeplink = popDeferredDeeplink()
      if (restoredDeeplink) {
        yield* put(
          DeeplinkActions.setDeferredDeeplink(restoredDeeplink.path, restoredDeeplink.props)
        )
      }
    }

    // Kill previous saga execution when LOGOUT action is put
    if (action?.type === LoginTypes.LOGOUT) {
      return
    }

    if (!loginHashDetected && peekDestinationOrigin() === 'seamless') {
      const connection = getPrimaryConnection()
      if (connection) {
        yield* put(LoginActions.setConnection(connection))
        yield* put(
          LoginActions.silentLoginRequestWithConnection(connection, {
            triggerLogoutOnFailure: false,
          })
        )
        return
      }
    }

    yield* spawn(Analytics.trackLoadingTime, { action: LAUNCH_CATEGORY.START })
    yield* put(LoginActions.setIsLoggingIn(false))
    yield* put(LoginActions.setIsOnboardingInProgress(false))
    yield* put(PatientActions.setChangeEmailToken({}))
    yield* call(featuresMonitor)

    const { accessToken } = yield* select(loginState)
    const supported = yield* call(appVersionIsSupported, DeviceInfo)

    if (!supported) {
      const failurePayload = createVersionNotSupportedFailure()

      yield* put(StartupActions.fullStartupFailure(failurePayload))
      yield* spawn(Analytics.trackLoadingTime, {
        action: LAUNCH_CATEGORY.EXIT,
        label: 'Not supported',
      })

      if (!isEmbeddedEnvironment()) {
        yield* call(Nav.reset, { index: 0, routes: [{ name: 'unsupportedVersion' }] })
      }

      return
    }

    if (!watchDeeplinkRequestsTask) {
      watchDeeplinkRequestsTask = yield* spawn(watchDeeplinkRequests)
      Analytics.trackLoadingTime({ action: 'deeplink request watched' })
    }

    /* Because the preload ensures that an access token is already available in the embedded environment, It causes us to enter this flow prematurely.

    Normally, this branch would only be entered once everything is already setup and you reenter the app — i.e. a standalone restart that rehydrated a persisted accessToken. In embedded mode, `preloaded` is true after the preload saga ran (and accessToken comes from there too), so we fall through to the login branch and let it run the full startup sequence.
    */
    const { preloaded } = yield* select(preloadState)
    if (accessToken && !preloaded) {
      const wasLoggedOut = yield* call(forcedSessionInactivity)

      if (wasLoggedOut) {
        Analytics.trackLoadingTime({
          action: LAUNCH_CATEGORY.EXIT,
          label: 'Forced session inactivity',
        })

        return
      }

      if (isEmbeddedEnvironment()) {
        yield* call(launchStartupSequence)
      } else if (isWeb()) {
        // Check if the user needs to be logged out due to an expired public session
        yield* call(checkPublicComputer)
        yield* call(launchStartupSequence)
      } else {
        yield* call(checkAppLock)
      }
    } else {
      //Reset app lock on explicit login
      yield* put(StartupActions.setAppLockToggle(false))

      if (isEmbeddedEnvironment()) {
        // Use launchStartupSequence (renewAuth) when either:
        //  - skipTokenExchange is set (VoIP cold-start: no exchange token available)
        //  - preloaded is true (preload already consumed the single-use exchange token)
        // Otherwise fall back to loginWithExchangeToken for a first-time embedded boot.
        const skipTokenExchange = yield* select(selectSkipTokenExchange)
        if (skipTokenExchange || preloaded) {
          yield* call(launchStartupSequence)
        } else {
          yield* call(loginWithExchangeToken)
        }
      } else {
        Analytics.trackLoadingTime({
          action: LAUNCH_CATEGORY.EXIT,
          label: 'Welcome redirection',
        })

        yield* call(Nav.reset, { index: 0, routes: [{ name: 'welcome' }] })
      }
    }

    if (isEmbeddedEnvironment()) {
      // TODO: For now, change it when embedded navigation is handled in native side. DIA-85527
      // Remove it after that.
      const currentRoute = Nav.getCurrentRoute()

      // Only reset to tabbar if navigation hasn't been initialized yet.
      // The startup flow (navigateWithMandatoryMfa) already handles navigation
      // to the correct screen — including deeplink destinations.
      if (!currentRoute || currentRoute.name === 'loading') {
        yield* call(waitForNavigationReady)
        yield* call(Nav.reset, { index: 0, routes: [{ name: 'tabbar' }] })
      }

      const { preloaded } = yield* select(preloadState)

      if (!preloaded) {
        // These below steps are also done during preloading

        const pushToken = yield* call(BrownfieldBridgeInternal.getPushToken)

        if (pushToken) {
          yield* put(StartupActions.setPushTokenRequest(pushToken, 5))
        } else {
          BrownfieldBridgeInternal.triggerEvent({
            type: 'PUSH_TOKEN_REGISTRATION_FAILURE',
            payload: {
              message: 'Regular push token not available',
              tokenType: 'regular',
            },
          })
        }

        if (isIOS()) {
          const voipPushToken = yield* call(BrownfieldBridgeInternal.getVoipPushToken)

          if (voipPushToken) {
            yield* put(StartupActions.setPushVoipTokenRequest(voipPushToken))
          } else {
            BrownfieldBridgeInternal.triggerEvent({
              type: 'VOIP_PUSH_TOKEN_REGISTRATION_FAILURE',
              payload: {
                message: 'VoIP push token not available',
                tokenType: 'voip',
              },
            })
          }
        }
      }
    }
    if (isEmbeddedEnvironment()) {
      // Process any pending events from the native side
      // that were emitted while the bridge was not connected
      BrownfieldBridgeInternal.reconnectBridge()
    }
    yield* put(StartupActions.fullStartupSuccess())
  } catch (err) {
    if ((err as any)?.code === MISSING_REFRESH_TOKEN_ERROR_CODE) {
      // renewAuth already dispatched logout — just swallow to prevent crashing the root saga.
      return
    }

    yield* put(AppSessionActions.setSystemError(err, true))
    Analytics.trackLoadingTime({
      action: LAUNCH_CATEGORY.EXIT,
      label: 'System error',
    })

    // Classify the error using centralized logic
    const failurePayload = classifyStartupFailure(err as Error)

    if (isEmbeddedEnvironment()) {
      // TODO: Workaround for embedded app to redirect to welcome screen on error. DIA-85528
      // This is useful for contributing that doesn't have SL Access via VPN.
      // Not optimal, to be removed once embedded navigation is handled in native side.

      // FIXME: this config value is not defined, remove this
      if (Config.EMBEDDED_STARTUP_REDIRECT_ON_ERROR) {
        yield* call(waitForNavigationReady)
        yield* call(Nav.reset, {
          index: 0,
          routes: [{ name: Config.EMBEDDED_STARTUP_REDIRECT_ON_ERROR }],
        })
      } else {
        BrownfieldBridgeInternal.popToNative(true)
      }
    }

    yield* put(StartupActions.fullStartupFailure(failurePayload))
  }
}

type RegisterTokenProps = {
  retryCount?: number
  pushToken: string
}
export function* registerPushToken({
  retryCount = 0,
  pushToken,
}: RegisterTokenProps): SagaIterator<void> {
  const patientProfile = yield* select(patientProfileState)
  const isLoggedIn = !!patientProfile.id
  if (!isLoggedIn) return

  const platform = Platform.OS
  const platformType = PLATFORM_PUSH_TYPES[platform]

  const deviceId = DeviceInfo.getUniqueId()
  const brandVersion = isEmbeddedEnvironment()
    ? yield* call(BrownfieldBridgeInternal.getEmbeddedBrandVersion)
    : Config.BRAND_VERSION || null

  if (!platformType) return

  const engageRes = yield* call(registerDeviceToken, {
    token: pushToken,
    device_id: deviceId,
    platform_type: platformType,
    brand_version: brandVersion,
  })

  if (!engageRes) return

  if (engageRes.status === 200) {
    yield* put(StartupActions.setPushTokenSuccess(pushToken))
    if (isEmbeddedEnvironment()) {
      BrownfieldBridgeInternal.triggerEvent({
        type: 'PUSH_TOKEN_REGISTRATION_SUCCESS',
        payload: {
          tokenType: 'regular',
        },
      })
    }
  } else {
    const errorCode = engageRes.problem || 'UNKNOWN_ERROR'
    const errMsg = `Failed to register push token ${pushToken}, with ${platformType}. Error: ${errorCode}`
    const shouldRetry = retryCount < REGISTER_PUSH_TOKEN_RETRY_LIMIT

    yield* put(StartupActions.setPushTokenFailure(pushToken, errMsg, retryCount))

    if (shouldRetry) {
      retryCount++
      const expoRetryDelay = Math.pow(2, retryCount) * 1000
      yield delay(expoRetryDelay)
      yield* call(registerPushToken, { pushToken, retryCount })
    } else {
      logger.error('Failed to register push token', { errorCode, platformType })
    }
  }
}

export function* gotoForeground() {
  logger.info('gotoForeground saga started')
  const { accessToken, isLoggingIn } = yield* select(loginState)
  if (!accessToken && !isLoggingIn) {
    logger.warn('auth:foreground_no_token')
    try {
      yield* all([
        put(PatientActions.patientProfileClear()),
        put(PatientHistoryActions.patientHistoryClear()),
        put(familyActions.familyClear()),
        call([Crypto, Crypto.randomUUID]),
        put(DeeplinkActions.setReadyToProcess(false)),
        sprigClient.logout(),
        put(LoginActions.clearState()),
        put(LoginActions.stopRefreshAuthPoll()),
        put(HabitActions.cancelAllHabitLocalNotifications()),
        put(memberTasks.reset()),
      ])
    } catch (_e) {
      // don't break root saga
    }
  }

  // Run the effects required for a partial startup for a logged in user
  if (accessToken && !isLoggingIn) {
    logger.info('auth:foreground_with_token')
    try {
      const wasLoggedOut = yield* call(forcedSessionInactivity)
      if (wasLoggedOut) return

      yield* call(waitForLDInitialization)
      const resynced = yield* call(renewAuth)
      if (resynced) return

      const { accessToken: currentAccessToken, customClaims: currentCustomClaims } = yield* select(
        loginState
      )
      yield* call(fetchUserInfo, currentAccessToken)
      const emailVerified = currentCustomClaims?.email_verified ?? false
      // TODO: - extract the various link status values into an enum since it's starting to pop up everywhere
      const ssoLinkStatus = currentCustomClaims?.sso_link_status

      if (!emailVerified) {
        // if the member's email is not verified, we skip the api fetches
        return
      }
      // if the user's sso link status is not linked, we skip the api fetches
      if (isEmbeddedEnvironment() && ssoLinkStatus && ssoLinkStatus !== 'linked') {
        return
      }

      yield* call(enableAppLock)
      yield* put(PatientActions.patientProfileFetchRequest())
      yield* spawn(updateLocation)

      yield* put(PatientHistoryActions.fetchChannelsRequest(false))
      // Proactively register device on launch
      yield* call(registerTokens)
      yield* put(LoginActions.startRefreshAuthPoll())
      yield* spawn(fetchAppointments)
      yield* spawn(getActiveMinutes)

      if (isIOS()) {
        yield* spawn(checkAppleHealthDisconnectedStatus)
      }
    } catch {
      // If the checkRenewAuth fails and logs the user out, we need to catch and do nothing
      // to avoid breaking the root saga
    }
  }
}

export function* gotoBackground() {
  const { accessToken } = yield* select(loginState)
  if (accessToken) {
    const timestamp = yield* call(getTimeSinceStartup)
    yield* put(AppSessionActions.setAppBackgroundedTimestamp(timestamp))
    yield* put(LoginActions.stopRefreshAuthPoll())
  }
}

export function* setIsConnected({ isConnected }: { isConnected: boolean }) {
  if (isConnected) {
    yield* put(PatientHistoryActions.fetchChannelsRequest(false))
  }
}
type RegisterPushVoipTokenProps = {
  retryCount?: number
  pushVoipToken: string
}
export function* registerPushVoipToken({
  retryCount = 0,
  pushVoipToken,
}: RegisterPushVoipTokenProps): SagaIterator<void> {
  // Only available for iOS APNS VoIP, for now
  if (Platform.OS !== 'ios') return

  const patientProfile = yield* select(patientProfileState)
  const isLoggedIn = !!patientProfile.id
  if (!isLoggedIn) return

  const deviceId = DeviceInfo.getUniqueId()
  const brandVersion: string | null = isEmbeddedEnvironment()
    ? yield* call(BrownfieldBridgeInternal.getEmbeddedBrandVersion)
    : Config.BRAND_VERSION || null

  const res = yield* call(registerDeviceToken, {
    token: pushVoipToken,
    device_id: deviceId,
    platform_type: PlatformType.ApnsVoip,
    brand_version: brandVersion,
  })

  if (!res) return

  if (res.status === 200) {
    yield* put(StartupActions.setPushVoipTokenSuccess(pushVoipToken))
    if (isEmbeddedEnvironment()) {
      BrownfieldBridgeInternal.triggerEvent({
        type: 'PUSH_TOKEN_REGISTRATION_SUCCESS',
        payload: {
          tokenType: 'voip',
        },
      })
    }
  } else {
    const errorCode = res.problem || 'UNKNOWN_ERROR'
    const errMsg = `Failed to register push token ${pushVoipToken}, with ${PlatformType.ApnsVoip}. Error: ${errorCode}`
    const shouldRetry = retryCount < REGISTER_PUSH_TOKEN_RETRY_LIMIT

    yield* put(StartupActions.setPushVoipTokenFailure(pushVoipToken, errMsg, retryCount))

    if (shouldRetry) {
      retryCount++
      const expoRetryDelay = Math.pow(2, retryCount) * 1000
      yield delay(expoRetryDelay)
      yield* call(registerPushVoipToken, { pushVoipToken, retryCount })
    } else {
      logger.error('Failed to register push VoIP token', { errorCode })
    }
  }
}

export function* unregisterPushToken() {
  try {
    yield* spawn(logoutInsiderUser)

    const engageClient = yield* call(getTokensApi)
    const deviceId = DeviceInfo.getUniqueId()

    if (!deviceId) {
      return
    }

    const deactivateRequest: DeactivateTokenRequestV2 = {
      brand_id: Config.BRAND_ID,
      device_id: deviceId,
      brand_version: Config.BRAND_VERSION || null,
    }

    yield* call(engageClient.v2PushTokensDeactivatePut, deactivateRequest)
  } catch (_e) {}
}

export function* toggleMandatoryMfa() {
  yield* put(LoginActions.mandatoryMfaRequest())
  yield* call(toggleMfa)
  const patient = yield* select(patientState)
  const isMfaEnabled = patient?.userInfo?.user_metadata?.mfa_enabled ?? false

  if (isMfaEnabled) {
    yield* put(StartupActions.mandatoryMfaToggled())
  }
}
